/**
 * components/profile/CheckInPromptCard.jsx — v3.7.10 Phase 84.31 Part 4
 *
 * Frames the "How you feel about your finances" question as a
 * temporary reflection rather than a permanent attribute. When the
 * user's last answer is 3+ weeks old (or has never been given), the
 * card asks them to check in again — with a clear "not now" escape
 * hatch that never nags twice in the same session.
 *
 * Renders itself null when the summary payload doesn't include a
 * `check_in` block (older backend or user has no answer yet AND
 * we don't want to force a first-time popup here — the LifeProfileEmbed
 * form below handles first-time capture).
 */
import React, { useState } from 'react';
import { Sparkles, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

function humanAge(days) {
  if (days == null) return 'a while ago';
  if (days < 1)    return 'just now';
  if (days < 7)    return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks  = Math.round(days / 7);
  if (days < 30)   return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

const DISMISS_KEY = 'twoone20:profileCheckInDismissedAt';

export default function CheckInPromptCard({ summary }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      const iso = window.sessionStorage.getItem(DISMISS_KEY);
      return !!iso;
    } catch { return false; }
  });

  const check = summary?.check_in;
  if (!check || dismissed) return null;

  // Only prompt when the last answer is genuinely stale. If we don't
  // have a timestamp yet (facts table unmigrated), show a gentle
  // "keep this fresh" line without the age copy.
  const stale     = check.stale === true;
  const daysKnown = typeof check.days_since === 'number';
  if (daysKnown && !stale) return null;

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch { /* ignore */ }
  };

  const jump = () => {
    const el = document.querySelector('[data-lp-field="financial_confidence"]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { el.focus({ preventScroll: true }); } catch { /* non-focusable */ }
    }
  };

  return (
    <Card
      className="rounded-2xl border-border/70 bg-amber-50/40 dark:bg-amber-950/10"
      data-testid="profile-checkin-card"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-charcoal">
              {daysKnown
                ? <>You last checked in <span className="font-semibold">{humanAge(check.days_since)}</span> — want to update?</>
                : <>How you feel about money changes over time. Want to give us a fresh check-in?</>}
            </div>
            <div className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
              Your last answer:{' '}
              <span className="text-charcoal">{check.value_pretty || check.value}</span>.
              This only nudges tone — it never changes any numbers.
            </div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={jump}
                className="text-primary text-[13px] hover:underline inline-flex items-center gap-1"
                data-testid="profile-checkin-update"
              >
                Update check-in <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="text-muted-foreground text-[12px] hover:text-charcoal inline-flex items-center gap-1"
                data-testid="profile-checkin-dismiss"
              >
                <Clock className="h-3 w-3" aria-hidden="true" /> Not now
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
